import { useEffect, useState, useMemo } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExternalLinkAlt, faCheck, faTimes, faChevronDown, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import PaginationBar from "../../Components/PaginationBar";
import NoResult from "../../Components/NoResult";
import Loader from "../../Components/Loader";
import PopUpModal from "../../Components/Modal";
import useApi from "../../Hooks/useApi";
import * as api from "../../api/getapi";
import * as postapi from "../../api/postapi";

const ListingReviewsApprovals = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [noOfPages, setNoOfPages] = useState(1);
  const [sizeOfPages, setSizeOfPages] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [orderBy, setOrderBy] = useState({ created_at: "DESC" });
  const [toggle, refresh] = useState(false);

  const [orderIdInput, setOrderIdInput] = useState("");

  const [orders, setOrders] = useState([]);
  const [totalItems, setTotalItems] = useState(0);

  const [selectedReviews, setSelectedReviews] = useState([]);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [reviewToReject, setReviewToReject] = useState(null);
  const [expandedReviews, setExpandedReviews] = useState([]);
  const [expandedOrders, setExpandedOrders] = useState([]);
  const [expandedOrderSources, setExpandedOrderSources] = useState([]);

  const fetchPendingListingReviews = useApi(api.fetchPendingListingReviews, { hasCatchError: true });
  const fetchPendingListingReviewsNoOfPages = useApi(api.fetchPendingListingReviewsNoOfPages, { hasCatchError: true });
  const approveListingReview = useApi(postapi.approveListingReview, { hasCatchError: true });
  const rejectListingReview = useApi(postapi.rejectListingReview, { hasCatchError: true });
  const bulkApproveListingReviews = useApi(postapi.bulkApproveListingReviews, { hasCatchError: true });
  const bulkRejectListingReviews = useApi(postapi.bulkRejectListingReviews, { hasCatchError: true });

  const parseOrderId = () => {
    const trimmed = String(orderIdInput || "").trim();
    if (!trimmed.length) return null;
    const asNumber = Number(trimmed);
    if (!Number.isFinite(asNumber)) return null;
    return asNumber;
  };

  const changePage = (page) => {
    setCurrentPage(page);
  };

  const resetFilterHandle = () => {
    setOrderIdInput("");
    setSizeOfPages(20);
    setOrderBy({ created_at: "DESC" });
  };

  const loadNoOfPages = async () => {
    const orderId = parseOrderId();

    const res = await fetchPendingListingReviewsNoOfPages.request({
      size: sizeOfPages,
      ...(typeof orderId === "number" ? { orderId } : {}),
    });

    const meta = res?.data?.data;
    setNoOfPages(meta?.no_of_pages ?? 0);
    setTotalItems(meta?.no_of_items ?? 0);
  };

  const loadReviews = async () => {
    const orderId = parseOrderId();

    const mapOrderByForApi = (orderObj) => {
      if (!orderObj) return orderObj;
      const [[key, value]] = Object.entries(orderObj);
      if (key === "listing_id") return { "l.listing_id": value };
      if (key === "order_number") return { "do.order_number": value };
      if (key === "customer_name") return { "do.customer_name": value };
      return { [`lr.${key}`]: value };
    };

    const res = await fetchPendingListingReviews.request({
      page: currentPage,
      size: sizeOfPages,
      orderBy: mapOrderByForApi(orderBy),
      ...(typeof orderId === "number" ? { orderId } : {}),
    });

    const data = res?.data?.data;
    setOrders(Array.isArray(data) ? data : []);
  };

  const toggleReviewExpansion = (reviewId) => {
    setExpandedReviews((prev) => {
      if (prev.includes(reviewId)) {
        return prev.filter((id) => id !== reviewId);
      } else {
        return [...prev, reviewId];
      }
    });
  };

  useEffect(() => {
    setIsLoading(true);
    Promise.all([loadNoOfPages(), loadReviews()])
      .catch(() => {
        setNoOfPages(0);
        setTotalItems(0);
        setOrders([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [currentPage, sizeOfPages, orderBy, toggle, orderIdInput]);

  useEffect(() => {
    if (currentPage === 1) {
      refresh((prev) => !prev);
    } else {
      changePage(1);
    }
  }, [sizeOfPages, orderBy, orderIdInput]);

  const handleApprove = async (row) => {
    try {
      await approveListingReview.request(row?.__raw?.id);
      toast.success("Review approved successfully", {
        position: "bottom-right",
        autoClose: 1500,
      });
      setSelectedReviews([]);
      refresh((prev) => !prev);
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || "Failed to approve review", {
        position: "bottom-right",
        autoClose: 1500,
      });
    }
  };

  const openRejectModal = (row) => {
    setReviewToReject(row);
    setRejectModalOpen(true);
  };

  const handleReject = async () => {
    try {
      await rejectListingReview.request(reviewToReject?.__raw?.id);
      toast.success("Review rejected successfully", {
        position: "bottom-right",
        autoClose: 1500,
      });
      setRejectModalOpen(false);
      setReviewToReject(null);
      setSelectedReviews([]);
      refresh((prev) => !prev);
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || "Failed to reject review", {
        position: "bottom-right",
        autoClose: 1500,
      });
    }
  };

  const handleBulkApprove = async () => {
    if (selectedReviews.length === 0) {
      toast.warning("Please select reviews to approve", {
        position: "bottom-right",
        autoClose: 1500,
      });
      return;
    }

    try {
      const res = await bulkApproveListingReviews.request({ ids: selectedReviews });
      toast.success("Reviews approved successfully", {
        position: "bottom-right",
        autoClose: 1500,
      });
      setSelectedReviews([]);
      refresh((prev) => !prev);
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || "Failed to approve reviews", {
        position: "bottom-right",
        autoClose: 1500,
      });
    }
  };

  const handleBulkReject = async () => {
    if (selectedReviews.length === 0) {
      toast.warning("Please select reviews to reject", {
        position: "bottom-right",
        autoClose: 1500,
      });
      return;
    }

    if (!window.confirm(`Are you sure you want to reject ${selectedReviews.length} review(s)?`)) {
      return;
    }

    try {
      const res = await bulkRejectListingReviews.request({ ids: selectedReviews });
      toast.success(res?.data?.data?.message || "Reviews rejected successfully", {
        position: "bottom-right",
        autoClose: 1500,
      });
      setSelectedReviews([]);
      refresh((prev) => !prev);
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || "Failed to reject reviews", {
        position: "bottom-right",
        autoClose: 1500,
      });
    }
  };

  const handleSelectReview = (reviewId) => {
    setSelectedReviews((prev) => {
      if (prev.includes(reviewId)) {
        return prev.filter((id) => id !== reviewId);
      } else {
        return [...prev, reviewId];
      }
    });
  };

  const allReviewIds = useMemo(() => {
    const ids = [];
    orders.forEach((order) => {
      order.order_sources.forEach((source) => {
        source.reviews.forEach((review) => {
          ids.push(review.id);
        });
      });
    });
    return ids;
  }, [orders]);

  const handleSelectAll = () => {
    if (selectedReviews.length === allReviewIds.length) {
      setSelectedReviews([]);
    } else {
      setSelectedReviews(allReviewIds);
    }
  };

  const getOrderReviewIds = (order) => {
    const ids = [];
    (order?.order_sources || []).forEach((s) => {
      (s.reviews || []).forEach((r) => ids.push(r.id));
    });
    return ids;
  };

  const getSourceReviewIds = (source) => {
    return (source?.reviews || []).map((r) => r.id);
  };

  const handleSelectOrder = (order, e) => {
    e && e.stopPropagation();
    const ids = getOrderReviewIds(order);
    const allSelected = ids.every((id) => selectedReviews.includes(id)) && ids.length > 0;
    setSelectedReviews((prev) => (allSelected ? prev.filter((id) => !ids.includes(id)) : [...new Set([...prev, ...ids])]));
  };

  const handleSelectSource = (source, e) => {
    e && e.stopPropagation();
    const ids = getSourceReviewIds(source);
    const allSelected = ids.every((id) => selectedReviews.includes(id)) && ids.length > 0;
    setSelectedReviews((prev) => (allSelected ? prev.filter((id) => !ids.includes(id)) : [...new Set([...prev, ...ids])]));
  };

  const expandAll = () => {
    const allOrderIds = orders.map((o) => o.order_id);
    const allSourceIds = orders.flatMap((o) => (o.order_sources || []).map((s) => s.order_source_id));
    setExpandedOrders(allOrderIds);
    setExpandedOrderSources(allSourceIds);
  };

  const collapseAll = () => {
    setExpandedOrders([]);
    setExpandedOrderSources([]);
  };

  const toggleOrderExpansion = (orderId) => {
    setExpandedOrders((prev) => {
      if (prev.includes(orderId)) {
        return prev.filter((id) => id !== orderId);
      } else {
        return [...prev, orderId];
      }
    });
  };

  const toggleSourceExpansion = (sourceId) => {
    setExpandedOrderSources((prev) => {
      if (prev.includes(sourceId)) {
        return prev.filter((id) => id !== sourceId);
      } else {
        return [...prev, sourceId];
      }
    });
  };

  return (
    <>
      <Container fluid>
        <Row className="d-flex align-items-center">
          <Col xs="6" lg="2" className="mb-3">
            <Form.Select className="bg-white" value={sizeOfPages} onChange={(e) => setSizeOfPages(Number(e.target.value))}>
              <option value="20">Showing 20</option>
              <option value="50">Showing 50</option>
              <option value="100">Showing 100</option>
            </Form.Select>
          </Col>

          <Col xs="12" lg="4" className="mb-3">
            <Form.Control
              className="bg-white"
              placeholder="Filter by Order ID"
              value={orderIdInput}
              onChange={(e) => {
                const next = e.target.value;
                if (/^\d*$/.test(next)) {
                  setOrderIdInput(next);
                }
              }}
            />
          </Col>

          <Col xs="12" lg="2" className="mb-3">
            <Button className="w-100" size="sm" onClick={resetFilterHandle}>
              Reset Filters
            </Button>
          </Col>
        </Row>

        {selectedReviews.length > 0 && (
          <Row className="mb-3">
            <Col>
              <div className="d-flex gap-2 align-items-center">
                <span className="text-muted">{selectedReviews.length} selected</span>
                <Button size="sm" variant="outline-primary" onClick={expandAll} title="Expand all">
                  Expand All
                </Button>
                <Button size="sm" variant="outline-secondary" onClick={collapseAll} title="Collapse all">
                  Collapse All
                </Button>
                <Button size="sm" variant="success" onClick={handleBulkApprove} disabled={selectedReviews.length === 0 || Boolean(bulkApproveListingReviews?.loading || bulkApproveListingReviews?.isLoading)}>
                  {Boolean(bulkApproveListingReviews?.loading || bulkApproveListingReviews?.isLoading) ? "Approving..." : "Bulk Approve"}
                </Button>
                <Button size="sm" variant="danger" onClick={handleBulkReject} disabled={selectedReviews.length === 0 || Boolean(bulkRejectListingReviews?.loading || bulkRejectListingReviews?.isLoading)}>
                  {Boolean(bulkRejectListingReviews?.loading || bulkRejectListingReviews?.isLoading) ? "Rejecting..." : "Bulk Reject"}
                </Button>
              </div>
            </Col>
          </Row>
        )}

        {isLoading ? (
          <div style={{ height: "50vh", display: "flex", justifyContent: "center" }}>
            <Loader />
          </div>
        ) : orders?.length === 0 ? (
          <NoResult name="Pending Reviews" />
        ) : (
          <>
            <div className="table-responsive mt-3">
              <table className="table table-bordered table-hover table-sm">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "40px" }}>
                      <Form.Check type="checkbox" checked={selectedReviews.length === allReviewIds.length && allReviewIds.length > 0} onChange={handleSelectAll} />
                    </th>
                    <th>Order #</th>
                    <th>Order Date</th>
                    <th>Customer</th>
                    <th>Order Total</th>
                    <th>Shops</th>
                    <th style={{ width: "80px" }}>Reviews</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const rows = [];
                    orders.forEach((order) => {
                      const orderIds = getOrderReviewIds(order);
                      const orderAllSelected = orderIds.length > 0 && orderIds.every((id) => selectedReviews.includes(id));
                      rows.push(
                        <tr key={`order-${order.order_id}`} onClick={() => toggleOrderExpansion(order.order_id)} style={{ cursor: "pointer", backgroundColor: "#f8f9fa" }}>
                          <td style={{ verticalAlign: "middle", width: 40 }}>
                            <Form.Check type="checkbox" checked={orderAllSelected} onChange={(e) => handleSelectOrder(order, e)} onClick={(e) => e.stopPropagation()} />
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <FontAwesomeIcon icon={expandedOrders.includes(order.order_id) ? faChevronDown : faChevronRight} />
                              <strong>{order.order_number}</strong>
                            </div>
                          </td>
                          <td>
                            <span className="text-nowrap">{order.order_date ? new Date(order.order_date).toLocaleDateString() : "N/A"}</span>
                          </td>
                          <td>
                            <div style={{ minWidth: "120px" }}>
                              <div>{order.customer_name}</div>
                              <small className="text-muted d-block">{order.customer_email}</small>
                              <small className="text-muted">{order.customer_phone}</small>
                            </div>
                          </td>
                          <td>
                            <div>{order.order_total}</div>
                          </td>
                          <td>
                            <span className="text-nowrap">{order.order_sources?.length || 0}</span>
                          </td>
                          <td className="text-center">
                            <span className="badge bg-secondary text-white">{orderIds.length}</span>
                          </td>
                        </tr>,
                      );
                      if (expandedOrders.includes(order.order_id)) {
                        order.order_sources.forEach((source) => {
                          const sourceIds = getSourceReviewIds(source);
                          const sourceAllSelected = sourceIds.length > 0 && sourceIds.every((id) => selectedReviews.includes(id));
                          rows.push(
                            <tr key={`source-${source.order_source_id}`} onClick={() => toggleSourceExpansion(source.order_source_id)} style={{ cursor: "pointer", backgroundColor: "#e9ecef" }}>
                              <td style={{ verticalAlign: "middle", width: 40 }}>
                                <Form.Check type="checkbox" checked={sourceAllSelected} onChange={(e) => handleSelectSource(source, e)} onClick={(e) => e.stopPropagation()} />
                              </td>
                              <td colSpan={8} style={{ paddingLeft: "40px" }}>
                                <FontAwesomeIcon icon={expandedOrderSources.includes(source.order_source_id) ? faChevronDown : faChevronRight} className="me-2" />
                                <strong style={{ color: "#8834ff" }}>Shop: {source.shop_name}</strong>
                                <div className="text-muted small">{(source.reviews || []).length} review(s)</div>
                              </td>
                            </tr>,
                          );
                          if (expandedOrderSources.includes(source.order_source_id)) {
                            rows.push(
                              <tr key={`reviews-header-${source.order_source_id}`}>
                                <td />
                                <td style={{ backgroundColor: "#cab9e1" }}>Listing</td>
                                <td style={{ backgroundColor: "#cab9e1" }}>Order Source #</td>
                                <td style={{ backgroundColor: "#cab9e1" }}>Seller</td>
                                <td style={{ width: "80px", backgroundColor: "#cab9e1" }}>Rating</td>
                                <td style={{ backgroundColor: "#cab9e1" }}>Review</td>
                                <td style={{ width: "100px", backgroundColor: "#cab9e1" }}>Actions</td>
                              </tr>,
                            );

                            source.reviews.forEach((review) => {
                              rows.push(
                                <tr key={`review-${review.id}`}>
                                  <td>
                                    <Form.Check type="checkbox" checked={selectedReviews.includes(review.id)} onChange={() => handleSelectReview(review.id)} />
                                  </td>
                                  <td>
                                    <div style={{ minWidth: "150px" }} className="d-flex align-items-center gap-2">
                                      <strong>{review.listing_title}</strong>
                                      {review.url && (
                                        <a href={review.url} target="_blank" rel="noopener noreferrer" className="text-primary">
                                          <FontAwesomeIcon icon={faExternalLinkAlt} size="sm" />
                                        </a>
                                      )}
                                    </div>
                                  </td>
                                  <td>
                                    <span className="text-nowrap">{source.order_source_id}</span>
                                  </td>
                                  <td>
                                    <span className="text-nowrap">{source.shop_name}</span>
                                  </td>
                                  <td className="text-center">
                                    <span className="badge bg-warning text-dark">{review.rating} ★</span>
                                  </td>
                                  <td>
                                    <div style={{ maxWidth: "250px", minWidth: "150px" }}>
                                      <div
                                        style={{
                                          display: "-webkit-box",
                                          WebkitLineClamp: expandedReviews.includes(review.id) ? "unset" : 2,
                                          WebkitBoxOrient: "vertical",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                        }}
                                      >
                                        {review.review}
                                      </div>
                                      {review.review && review.review.length > 100 && (
                                        <Button variant="link" size="sm" className="p-0 text-decoration-none" onClick={() => toggleReviewExpansion(review.id)}>
                                          {expandedReviews.includes(review.id) ? "Show less" : "Show more"}
                                        </Button>
                                      )}
                                      {(review.img0 || review.img1) && (
                                        <div className="d-flex gap-1 mt-1">
                                          {review.img0 && (
                                            <a href={`${process.env.REACT_APP_URL_S3}/${review.img0}`} target="_blank" rel="noopener noreferrer">
                                              <img src={`${process.env.REACT_APP_URL_S3}/${review.img0}`} alt="review img 1" style={{ width: 60, height: 60, objectFit: "cover", borderRadius: 4, border: "1px solid #dee2e6" }} />
                                            </a>
                                          )}
                                          {review.img1 && (
                                            <a href={`${process.env.REACT_APP_URL_S3}/${review.img1}`} target="_blank" rel="noopener noreferrer">
                                              <img src={`${process.env.REACT_APP_URL_S3}/${review.img1}`} alt="review img 2" style={{ width: 60, height: 60, objectFit: "cover", borderRadius: 4, border: "1px solid #dee2e6" }} />
                                            </a>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </td>

                                  <td>
                                    <div className="d-flex gap-2 justify-content-center">
                                      <Button size="sm" variant="outline-success" onClick={() => handleApprove({ __raw: review })} style={{ width: "32px", height: "32px", padding: "0", display: "flex", alignItems: "center", justifyContent: "center" }} title="Approve">
                                        <FontAwesomeIcon icon={faCheck} />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline-danger"
                                        onClick={() => openRejectModal({ __raw: review, listing_title: review.listing_title, customer_name: review.customer_name, rating: review.rating, review: review.review })}
                                        style={{ width: "32px", height: "32px", padding: "0", display: "flex", alignItems: "center", justifyContent: "center" }}
                                        title="Reject"
                                      >
                                        <FontAwesomeIcon icon={faTimes} />
                                      </Button>
                                    </div>
                                  </td>
                                </tr>,
                              );
                            });
                          }
                        });
                      }
                    });
                    return rows;
                  })()}
                </tbody>
              </table>
            </div>
            <Row className="mt-5 align-items-center">
              <Col className="d-none d-md-block" md="3" lg="3">
                <span className="text-secondary fs-6">{totalItems} result(s)</span>
              </Col>
              <Col xs="12" md="6" lg="6">
                <PaginationBar currentPage={currentPage} noOfPages={noOfPages} changePage={changePage} />
              </Col>
              <Col className="d-md-none d-flex align-items-center mt-3" xs="6" lg="3">
                <span className="text-secondary fs-6">{totalItems} result(s)</span>
              </Col>
            </Row>
          </>
        )}
      </Container>

      <PopUpModal
        title="Confirm Rejection"
        show={rejectModalOpen}
        onHide={() => {
          setRejectModalOpen(false);
          setReviewToReject(null);
        }}
      >
        <div>
          <p>Are you sure you want to reject this review?</p>
          {reviewToReject && (
            <div className="border p-3 rounded mb-3">
              <p>
                <strong>Listing:</strong> {reviewToReject.listing_title}
              </p>
              <p>
                <strong>Customer:</strong> {reviewToReject.customer_name}
              </p>
              <p>
                <strong>Rating:</strong> {reviewToReject.rating} ★
              </p>
              <p>
                <strong>Review:</strong> {reviewToReject.review}
              </p>
              {(reviewToReject.img0 || reviewToReject.img1) && (
                <div>
                  <strong>Images:</strong>
                  <div className="d-flex gap-2 mt-1">
                    {reviewToReject.img0 && (
                      <a href={`${process.env.REACT_APP_URL_S3}/${reviewToReject.img0}`} target="_blank" rel="noopener noreferrer">
                        <img src={`${process.env.REACT_APP_URL_S3}/${reviewToReject.img0}`} alt="review img 1" style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 4, border: "1px solid #dee2e6" }} />
                      </a>
                    )}
                    {reviewToReject.img1 && (
                      <a href={`${process.env.REACT_APP_URL_S3}/${reviewToReject.img1}`} target="_blank" rel="noopener noreferrer">
                        <img src={`${process.env.REACT_APP_URL_S3}/${reviewToReject.img1}`} alt="review img 2" style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 4, border: "1px solid #dee2e6" }} />
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          <Row>
            <Col>
              <Button
                variant="outline-secondary"
                className="w-100"
                onClick={() => {
                  setRejectModalOpen(false);
                  setReviewToReject(null);
                }}
              >
                Cancel
              </Button>
            </Col>
            <Col>
              <Button variant="danger" className="w-100" onClick={handleReject}>
                Reject
              </Button>
            </Col>
          </Row>
        </div>
      </PopUpModal>
    </>
  );
};

export default ListingReviewsApprovals;
